import React, { FormEvent, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faFileLines, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import AmpButton from "../../common/AmpButton/AmpButton";
import { RootState } from "../../../redux/reducers/root";
import { RESET_DOCUMENT } from "../../../redux/actionTypes/actionsTypes";
import { getDocument } from "../../../redux/actions/document/document";
import { fileRfr } from "../../../redux/actions/rfr/rfr";
import ReactGA from "react-ga4";

import "./Notice.css";
import Tooltip from "../../common/ToolTip/ToolTip";

interface NoticeProps {
    notice: Record<string, unknown>,
    state: Record<string, unknown>
}

export default function Notice(props: NoticeProps): JSX.Element {
    // eslint-disable-next-line
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation("rfr");

    const fileRfrEvent = (e: FormEvent) => {
        e.preventDefault();
        const data: FormData = new FormData(e.target as HTMLFormElement);
        // eslint-disable-next-line
        const values = Object.fromEntries(data.entries()) as Record<string, unknown>;
        // Send a custom event
        ReactGA.event({
            category: "RFR",
            action: "RFR Begin",
            label: `${values["roll"]}-${values["productid"]} RFR Begun`, // optional
        });
        dispatch(fileRfr(values));
    }

    const getTooltipContent = (key: string): JSX.Element => {
        return <>
            <div>
                <Trans defaults={t(key)}>
                    <Link className="amp-tooltip-anchor" rel="noopener noreferrer"></Link>
                    <p></p>
                </Trans>
            </div>
        </>;
    }

    const taxyear = props.notice.taxyear;
    const type = props.notice.vendorproducttype;
    const typekey = "rfr.notices.type." + type;
    const displayyear = props.notice.displayyear;
    const maildate = props.notice.maildate ? new Date(props.notice.maildate) : null;
    const deadlinedate = new Date(props.notice.deadlinedate);
    const link = props.notice.link;

    const documentState = useSelector((state: RootState) => state.docReducer.document);
    const [selectedDocId, setSelectedDocId] = useState("");

    const downloadNotice = (e) => {
        e.preventDefault();
        const downloadInfo = {
            "roll": props.notice.rollnumber,
            "is_report": false,
            "is_meth": false,
            "doc_name": null,
            "doc_id": props.notice.id,
            "property_id": null
        }
        dispatch(getDocument(downloadInfo));
        setSelectedDocId(props.notice.id as string);
    }

    useEffect(() => {
        if (documentState.document != null && documentState.document.pdf != null && !documentState.loading) {
            if (documentState.document.data.doc_id === selectedDocId) {
                const url = documentState.document.pdf;
                const pathArray = props.notice.documentpath ? (props.notice?.documentpath as string).split("/") : "";
                const filename = pathArray[pathArray.length - 1];
                const a = document.createElement("a");
                a.href = url;
                a.download = filename !== "" ? filename : "Notice.pdf";
                a.click();
                dispatch({ type: RESET_DOCUMENT })
                setSelectedDocId("");
            }
        }
    }, [documentState, props.notice.documentpath, selectedDocId, dispatch]);

    let statuselem;
    if (props.notice.event_code && props.notice.status_text) {
        const tooltip = props.notice.tooltip_text;
        if (tooltip && props.notice.tooltipvisible) {
            statuselem =
                <div>
                    <Tooltip
                        content={getTooltipContent(tooltip)}
                        direction={"top"}
                        alignment={"left-text"}
                    >
                        <span className="greenyellow rfr-notice-table-tooltip rfr-notice-table-tooltip-underline" data-tip data-for={"statustooltip" + taxyear + type}>{props.notice.status_text}</span>
                    </Tooltip>
                </div>
        } else {
            statuselem = <span className="greenyellow rfr-notice-table-tooltip rfr-notice-table-tooltip-underline">{props.notice.status_text}</span>
        }
    }
    else if (props.notice.submission_date) {
        const submission_date = new Date(props.notice.submission_date);
        const statustip = t("rfr.notices.status.submitted-tooltip") + new Intl.DateTimeFormat(i18n.language, {
            timeZone: 'UTC',
            year: "numeric",
            month: "long",
            day: "2-digit"
        }).format(submission_date) + "\n" + t("rfr.notices.status.submitted-tooltip-2");
        statuselem =
            <div>
                <Tooltip
                    content={getTooltipContent(statustip)}
                    direction={"top"}
                    alignment={"left-text"}
                >
                    <span className="greenyellow help rfr-notice-table-tooltip rfr-notice-table-tooltip-underline" data-tip data-for={"submittedtooltip" + taxyear + type}>
                        {t("rfr.notices.status.submitted")}
                    </span>
                </Tooltip>
            </div>
    }
    else if (true === props.notice.amended) {
        statuselem =
            <div>
                <Tooltip
                    content={getTooltipContent("rfr.notices.status.amended-tooltip")}
                    direction={"top"}
                    alignment={"left-text"}
                >
                    <span className="greenyellow help rfr-notice-table-tooltip rfr-notice-table-tooltip-underline" data-tip data-for={"amendedtooltip" + taxyear + type}>
                        {t("rfr.notices.status.amended")}
                    </span>
                </Tooltip>
            </div>
    }
    else if (props.notice.deadlinepassed) {
        statuselem =
            <div>

                <Tooltip
                    content={getTooltipContent("rfr.notices.nonotices")}
                    direction={"top"}
                    alignment={"left-text"}
                >
                    <span className="greenyellow help rfr-notice-table-tooltip rfr-notice-table-tooltip-underline" data-for={"deadline-tooltip-" + props.notice.vendorproductid} data-tip>
                        {t("rfr.notices.status.deadline-passed")} <FontAwesomeIcon className="tooltip" icon={faQuestionCircle} />
                    </span>
                </Tooltip>
            </div>
    }
    else if (type === "SAN") {
        statuselem =
            <div>
                <Tooltip
                    content={getTooltipContent("rfr.notices.status.san-submit-manually-tooltip")}
                    direction={"top"}
                    alignment={"left-text"}
                >
                    <span className="greenyellow help rfr-notice-table-tooltip rfr-notice-table-tooltip-underline" data-tip data-for={"santooltip" + taxyear + type}>
                        <a href={t("rfr.notices.status.san-form-link")} target="_blank" rel="noopener noreferrer">
                            {t("rfr.notices.status.printable-form")}
                        </a>
                    </span>
                </Tooltip>
            </div>
    } else {
        statuselem =
            <form onSubmit={(e) => { fileRfrEvent(e) }}>
                <input type="hidden" name="productid" value={props.notice.vendorproductid} />
                <input type="hidden" name="roll" value={props.notice.rollnumber} />
                <AmpButton type="submit" onClick={() => null} text={t("rfr.notices.status.file-an-rfr")} />
            </form>
    }

    return (
        <>
            <tr className="rfr-notice-table-border">
                <td className="rfr-notice-table-td-padding-left-15px" headers="rfr-notice-name">
                    {i18n.language === 'en' ? displayyear : ''} {t(typekey)} {i18n.language === 'fr' ? displayyear : ''}
                </td>
                <td className="rfr-ext-link" headers="rfr-notice-link">
                    {link ?
                        <a href="/" onClick={(e) => downloadNotice(e)}>
                            <FontAwesomeIcon icon={faFileLines} size="2x" className="rfr-notice-table-img-info-height" />
                        </a>
                        :
                        <div>
                            <Tooltip
                                content={getTooltipContent("rfr.notices.view-notice-tooltip")}
                                direction={"top"}
                                alignment={"left-text"}
                            >
                                <span data-tip data-for={"property-tooltip-" + props.notice.vendorproductid}>
                                    <Link className="amp-anchor" to="/amp/property" aria-label="view-property-notices">
                                        <FontAwesomeIcon icon={faCircleInfo} size="2x" className="rfr-notice-table-img-info-height" />
                                    </Link>
                                </span>
                            </Tooltip>
                        </div>
                    }
                </td>
                <td className="" headers="rfr-notice-date">
                    {maildate ?
                        new Intl.DateTimeFormat(i18n.language, {
                            timeZone: 'UTC',
                            year: "numeric",
                            month: "long",
                            day: "2-digit"
                        }).format(maildate)
                        :
                        t("rfr.notices.notapplicable")
                    }
                </td>
                <td className="" headers="rfr-notice-deadline">
                    {new Intl.DateTimeFormat(i18n.language, {
                        timeZone: 'UTC',
                        year: "numeric",
                        month: "long",
                        day: "2-digit"
                    }).format(deadlinedate)}
                </td>
                <td className="" headers="rfr-notice-status">
                    {statuselem}
                </td>
            </tr>
            {
                props.state !== null ?
                    (
                        props.notice.vendorproductid === props.state.noticeid ?
                            (
                                <tr className="rfr-notice-table-submitted-confirmation rfr-notice-table-border">
                                    <td colSpan={5} className="rfr-notice-table-td-padding-left-15px" headers="rfr-notice-status">
                                        {
                                            props.state.success ?
                                                (
                                                    t("rfr.notices.submit-confirm")
                                                )
                                                :
                                                (
                                                    <>
                                                        <p>{t("rfr.notices.submit-error-1")}</p>
                                                        <p>{t("rfr.notices.submit-error-2")}</p>
                                                    </>
                                                )
                                        }
                                    </td>
                                </tr>
                            )
                            :
                            <></>
                    )
                    :
                    <></>
            }
        </>
    );
}